/**
 * Look up: one field for phone, DBA, legal name, owner, contact or email (a MID still works, but
 * nobody asks a merchant for one, so the copy never suggests it) against the
 * verified account list. Live accounts first; closed ones counted and one click away. Type,
 * arrow, Enter opens the account. Arrowing onto a row prefetches it so Enter is instant.
 */
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type AccountHit, type AccountSearchOutput } from '@lsc/contract';
import { type CallerResolution } from '@lsc/domain';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Resolution, resolutionAccounts } from '../components/lookup/resolution';
import { StatusWord } from '../components/ui/status';
import { phone, processorName } from '../lib/format';
import { openCapture } from '../components/capture/capture-store';
import { FOCUS_SEARCH } from '../lib/keys';
import { lookupPlan, mergeLookupRows, rowId, type LookupPlan } from '../lib/lookup-plan';
import { accountQuery, resolveQuery, searchQuery } from '../lib/queries';
import { describeRpcError } from '../lib/rpc';

export const Route = createFileRoute('/')({
  component: Lookup,
});

const MIN_QUERY = 3;
const DEBOUNCE_MS = 120;

function useDebounced<T>(value: T, ms: number): [T, () => void] {
  const [v, setV] = useState(value);
  const pending = useRef(value);
  pending.current = value;
  useEffect(() => {
    const t = setTimeout(() => {
      setV(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return [v, () => { setV(pending.current); }];
}

function Lookup() {
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [debounced, flush] = useDebounced(q.trim(), DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY;

  // The plan follows the debounced value, the same text the queries run on, so the tenth digit
  // never fires a resolve for the nine-digit key that is still in flight.
  const plan = lookupPlan(debounced);
  const resolving = enabled && plan.resolving;
  const searching = enabled && plan.searching;
  // No placeholder on the resolver: a stale resolution is another caller, and Enter would open it.
  const resolve = useQuery({ ...resolveQuery(debounced), enabled: resolving });
  const resolution: CallerResolution | undefined = resolving ? resolve.data : undefined;
  const search = useQuery({ ...searchQuery(debounced, includeInactive), enabled: searching, placeholderData: keepPreviousData });
  const data: AccountSearchOutput | undefined = searching ? search.data : undefined;
  // Keyboard order: the resolution's locations first, then the table (minus what the resolver already lists).
  const resolved = resolution ? resolutionAccounts(resolution) : [];
  const rows = mergeLookupRows(resolved, data?.rows ?? []);
  const total = resolved.length + rows.length;

  useEffect(() => {
    setCursor(0);
  }, [debounced, includeInactive]);

  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    window.addEventListener(FOCUS_SEARCH, focus);
    return () => {
      window.removeEventListener(FOCUS_SEARCH, focus);
    };
  }, []);

  const atCursor = cursor < resolved.length ? resolved[cursor]?.accountId : rows[cursor - resolved.length]?.accountId;

  // The highlighted row is the one most likely to be opened: warm it.
  useEffect(() => {
    if (atCursor) void qc.query(accountQuery(atCursor)).catch(() => undefined);
  }, [atCursor, qc]);

  const openId = (accountId: string) => {
    void navigate({ to: '/accounts/$accountId', params: { accountId } });
  };
  const open = (hit: AccountHit) => {
    openId(hit.accountId);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(total - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      if (!enabled) flush();
      if (atCursor) openId(atCursor);
    } else if (e.key === 'Escape') {
      if (q) setQ('');
      else e.currentTarget.blur();
      setIncludeInactive(false);
    }
  };

  const hero = q.trim().length === 0;

  return (
    <div className={`mx-auto w-full max-w-[1120px] px-6 ${hero ? 'pt-[18vh]' : 'pt-6'} pb-10 transition-[padding] duration-150`}>
      <div className={hero ? 'mx-auto max-w-[640px]' : ''}>
        <label htmlFor="who" className={`block text-ink-2 ${hero ? 'mb-3 text-title' : 'mb-1.5 text-body'}`}>
          Who's calling?
        </label>
        <div className="relative">
          <input
            id="who"
            ref={inputRef}
            className={`mono w-full rounded-control border border-line bg-surface-2 px-4 text-ink placeholder:text-ink-3 focus:border-info focus-visible:outline-none ${hero ? 'h-12 text-title' : 'h-10 text-emphasis'}`}
            placeholder="phone, store, owner, or email"
            role="combobox"
            aria-expanded={total > 0}
            aria-controls="lookup-results"
            aria-activedescendant={atCursor ? rowId(atCursor) : undefined}
            aria-autocomplete="list"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
            }}
            onKeyDown={onKey}
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
          {hero && <kbd className="absolute top-1/2 right-3 -translate-y-1/2">/</kbd>}
        </div>
        <StatusLine
          q={q}
          cls={plan.kind}
          data={data}
          resolution={resolution}
          loading={search.isFetching || resolve.isFetching}
          error={search.error ?? resolve.error}
          includeInactive={includeInactive}
          onToggleInactive={() => {
            setIncludeInactive((v) => !v);
          }}
        />
      </div>

      <div id="lookup-results">
      {resolution && (
        <Resolution
          r={resolution}
          cursor={cursor}
          onHover={(i) => {
            setCursor(i);
          }}
          onOpen={openId}
          draftActions={
            resolution.status === 'none' && resolution.draft
              ? {
                  onStart: () => {
                    openCapture({ draftId: resolution.draft?.draftId ?? '', phone: q.trim() });
                  },
                  onLink: () => {
                    void navigate({ to: '/cleanup' });
                  },
                }
              : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <ResultsTable
          rows={rows}
          cursor={cursor - resolved.length}
          onHover={(i) => {
            setCursor(i + resolved.length);
          }}
          onOpen={open}
        />
      )}
      </div>
    </div>
  );
}

interface StatusLineProps {
  q: string;
  cls: LookupPlan['kind'];
  data: AccountSearchOutput | undefined;
  resolution: CallerResolution | undefined;
  loading: boolean;
  error: unknown;
  includeInactive: boolean;
  onToggleInactive: () => void;
}

function StatusLine({ q, cls, data, resolution, loading, error, includeInactive, onToggleInactive }: StatusLineProps) {
  const trimmed = q.trim();
  const base = 'mt-2 flex min-h-5 flex-wrap items-center gap-x-3 text-hint';
  if (trimmed.length === 0) {
    return (
      <p className={`${base} text-ink-3`}>
        Closed accounts stay out of the way until you ask. <kbd>↑</kbd> <kbd>↓</kbd> to move, <kbd>↵</kbd> to open.
      </p>
    );
  }
  if (trimmed.length < MIN_QUERY) return <p className={`${base} text-ink-3`}>{MIN_QUERY - trimmed.length} more to search</p>;
  if (error) return <p className={`${base} text-alert`}>{describeRpcError(error)}</p>;
  if (resolution && !data) {
    const n = resolution.status === 'one' ? 1 : resolution.status === 'many' ? resolution.total : 0;
    return (
      <p className={`${base} text-ink-2`}>
        <span className={loading ? 'opacity-60' : ''}>
          <span className="font-medium text-ink">{n}</span> {n === 1 ? 'location' : 'locations'} on this line
        </span>
        <span className="text-ink-3">read as phone</span>
      </p>
    );
  }
  if (!data) return <p className={`${base} text-ink-3`}>{resolution === undefined && cls === 'phone' ? 'Looking up the number' : 'Searching'}</p>;
  const digitsOnly = /^\d+$/.test(trimmed.replace(/[\s().+-]/g, ''));
  // Digits that are not a phone are a MID; reps never have one, so that reading stays unlabelled.
  const reading = cls === 'phone' || cls === 'ambiguous' ? 'phone' : cls === 'account' && !digitsOnly ? 'name or email' : '';
  const hidden = data.hiddenInactive;
  return (
    <p className={`${base} text-ink-2`}>
      <span className={loading ? 'opacity-60' : ''}>
        <span className="font-medium text-ink">{data.total}</span> {data.total === 1 ? 'account' : 'accounts'}
        {data.truncated && <span className="text-attention"> (showing {data.rows.length})</span>}
      </span>
      {reading && <span className="text-ink-3">read as {reading}</span>}
      {hidden > 0 && !includeInactive && (
        <button type="button" className="text-info hover:underline" onClick={onToggleInactive}>
          Show {hidden} closed
        </button>
      )}
      {includeInactive && (
        <button type="button" className="text-info hover:underline" onClick={onToggleInactive}>
          Hide closed
        </button>
      )}
    </p>
  );
}

interface ResultsTableProps {
  rows: AccountHit[];
  cursor: number;
  onHover: (i: number) => void;
  onOpen: (hit: AccountHit) => void;
}

function ResultsTable({ rows, cursor, onHover, onOpen }: ResultsTableProps) {
  return (
    <div className="mt-4 overflow-hidden rounded-panel border border-line bg-surface">
      <table className="w-full text-body" role="listbox" aria-label="Matching accounts">
        <thead className="text-left text-hint text-ink-3">
          <tr className="border-b border-line">
            <th className="w-[34%] px-4 py-2 font-normal">Account</th>
            <th className="px-3 py-2 font-normal">MID</th>
            <th className="px-3 py-2 font-normal">Processor</th>
            <th className="px-3 py-2 font-normal">Status</th>
            <th className="px-3 py-2 font-normal whitespace-nowrap">Support line</th>
            <th className="px-3 py-2 font-normal">City</th>
            <th className="px-3 py-2 font-normal">Matched</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const selected = i === cursor;
            return (
              <tr
                key={r.accountId}
                id={rowId(r.accountId)}
                role="option"
                aria-selected={selected}
                className={`cursor-pointer border-t border-line ${selected ? 'bg-surface-2' : 'hover:bg-surface-2/60'}`}
                onMouseEnter={() => {
                  onHover(i);
                }}
                onClick={() => {
                  onOpen(r);
                }}
              >
                <td className={`px-4 py-2 border-l-[3px] ${selected ? 'border-info' : 'border-transparent'}`}>
                  <div className="font-medium text-ink">{r.label}</div>
                  {r.legalName && r.legalName !== r.label && <div className="text-hint text-ink-3">{r.legalName}</div>}
                </td>
                <td className="mono whitespace-nowrap px-3 py-2 text-ink-2">{r.mid ?? <span className="text-ink-3">pending</span>}</td>
                <td className="whitespace-nowrap px-3 py-2 text-ink-2">
                  {processorName(r.processor)}
                  {r.env !== 'production' && <span className="ml-1 text-hint text-attention">{r.env}</span>}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <StatusWord status={r.status} />
                </td>
                <td className="mono whitespace-nowrap px-3 py-2 text-ink-2">{phone(r.csPhone) ?? <span className="text-ink-3">none</span>}</td>
                <td className="whitespace-nowrap px-3 py-2 text-ink-2">
                  {r.city ?? <span className="text-ink-3">?</span>}
                  {r.zip && r.city ? <span className="text-ink-3"> {r.zip}</span> : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-hint text-ink-3">
                  <MatchedOn hit={r} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const MATCH_LABEL: Record<AccountHit['matchedOn'][number], string> = {
  phone: 'phone',
  mid: 'MID',
  name: 'name',
  contact: 'contact',
  owner: 'owner',
  email: 'email',
};

function MatchedOn({ hit }: { hit: AccountHit }) {
  const kinds = hit.matchedOn.map((k) => MATCH_LABEL[k]).join(', ');
  const detailKind = hit.matchedOn.find((k) => k === 'contact' || k === 'owner' || k === 'email' || k === 'phone');
  const showDetail = hit.matchDetail && detailKind && hit.matchDetail !== hit.label;
  return (
    <span>
      {kinds}
      {showDetail && <span className="text-ink-2">: {hit.matchDetail}</span>}
    </span>
  );
}
